import { SiteFooter } from "@/components/site-footer";
import { SiteNav } from "@/components/site-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import siteData from "@/config/siteData.json";
import { auth } from "@/lib/auth";
import { generateMeta } from "@forge42/seo-tools/remix/metadata";
import { Bot, ShieldCheck, Sparkles, Terminal } from "lucide-react";
import {
  Link,
  useLoaderData,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "react-router";

export const meta: MetaFunction = () => {
  return generateMeta({
    title: "SendKit MCP for AI agents",
    description:
      "Connect Claude, ChatGPT, or any MCP client to SendKit. Agents can design on-brand newsletters, preview them, and schedule campaigns with your approval.",
    url: siteData.url + "/docs/mcp",
    image: siteData.url + "/sendkit-logo.png",
    siteName: siteData.name,
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await auth.api.getSession({
    headers: request.headers,
  });

  return { isLoggedIn: Boolean(session) };
}

const scopes = [
  {
    scope: "read",
    allows:
      "Brand, block schema, newsletters, analytics, segments, audience summary, templates, products, quota",
  },
  {
    scope: "draft",
    allows: "Create, update, preview, duplicate, and delete newsletters",
  },
  {
    scope: "send",
    allows: "Send test, schedule, and pause campaigns",
  },
];

const tools = [
  {
    group: "Read",
    items: [
      "get_brand",
      "get_block_schema",
      "list_newsletters",
      "get_newsletter",
      "get_newsletter_analytics",
      "list_segments",
      "get_audience_summary",
      "list_templates",
      "get_template",
      "list_products",
      "get_quota",
      "get_usage_guide",
    ],
  },
  {
    group: "Draft",
    items: [
      "create_newsletter",
      "update_newsletter",
      "render_newsletter_preview",
      "duplicate_newsletter",
      "delete_newsletter",
    ],
  },
  {
    group: "Send",
    items: ["send_test_newsletter", "schedule_newsletter", "pause_newsletter"],
  },
];

export default function McpDocs() {
  const { isLoggedIn } = useLoaderData<typeof loader>();
  const endpoint = `${siteData.url}/mcp`;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteNav isLoggedIn={isLoggedIn} />
      <main className="flex-1">
        <section className="container py-16 md:py-24">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1 text-sm">
              <Bot className="h-4 w-4" />
              SendKit MCP
            </div>
            <h1 className="text-5xl font-bold tracking-tight">
              Let AI agents run your email marketing.
            </h1>
            <p className="text-xl text-muted-foreground">
              Connect Claude, ChatGPT, or any MCP client to SendKit. Agents can
              design on-brand newsletters, preview them, and schedule campaigns
              with your approval.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link
                  to={isLoggedIn ? "/dashboard/settings/api" : "/signup"}
                >
                  {isLoggedIn ? "Create an API key" : "Get started"}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="/docs/mcp.md" target="_blank" rel="noreferrer">
                  Raw markdown
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="border-t bg-muted/40">
          <div className="container grid gap-8 py-16 md:grid-cols-3">
            <div className="space-y-3">
              <Sparkles className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold">Brand-aware design</h2>
              <p className="text-muted-foreground">
                Agents read your theme, logo, header, and footer, then build
                newsletters with structured blocks that stay editable.
              </p>
            </div>
            <div className="space-y-3">
              <Terminal className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold">Preview before sending</h2>
              <p className="text-muted-foreground">
                Render drafts, check warnings and spam scores, and send tests
                before anything reaches your audience.
              </p>
            </div>
            <div className="space-y-3">
              <ShieldCheck className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold">Human-approved scheduling</h2>
              <p className="text-muted-foreground">
                Scheduling requires explicit confirmation and returns recipient
                count and quota impact first.
              </p>
            </div>
          </div>
        </section>

        <section className="container grid gap-12 py-16 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-14">
            <section id="endpoint" className="space-y-4">
              <h2 className="text-3xl font-bold">Endpoint and authentication</h2>
              <p className="text-muted-foreground">
                SendKit exposes a stateless Streamable HTTP MCP server. Every
                request uses the public API key as the identifier and the secret
                key as the credential. Custom clients can send an HMAC{" "}
                <code className="font-mono">X-Signature</code> over the raw
                request body instead of <code className="font-mono">X-Secret-Key</code>.
              </p>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                {`${endpoint}\nAuthorization: Bearer pk_your_public_key\nX-Secret-Key: sk_your_secret_key`}
              </pre>
            </section>

            <section id="scopes" className="space-y-4">
              <h2 className="text-3xl font-bold">Scopes</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-3 pr-4 font-semibold">Scope</th>
                      <th className="py-3 font-semibold">Allows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scopes.map((item) => (
                      <tr key={item.scope} className="border-b align-top">
                        <td className="py-3 pr-4 font-mono">{item.scope}</td>
                        <td className="py-3 text-muted-foreground">
                          {item.allows}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="tools" className="space-y-6">
              <h2 className="text-3xl font-bold">Tools</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {tools.map((group) => (
                  <Card key={group.group}>
                    <CardHeader>
                      <CardTitle>{group.group}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        {group.items.map((item) => (
                          <li key={item} className="font-mono">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section id="blocks" className="space-y-4">
              <h2 className="text-3xl font-bold">Design with blocks</h2>
              <p className="text-muted-foreground">
                Blocks are the recommended design mode. SendKit renders them
                with your theme, logo, header, and footer, and the result stays
                editable in the visual editor.
              </p>
              <ol className="list-decimal space-y-2 pl-6 text-muted-foreground">
                <li>
                  Call <code className="font-mono">get_brand</code> and{" "}
                  <code className="font-mono">get_block_schema</code>.
                </li>
                <li>
                  Create a draft with{" "}
                  <code className="font-mono">content.mode = &quot;blocks&quot;</code>.
                </li>
                <li>
                  Call <code className="font-mono">render_newsletter_preview</code>{" "}
                  and review warnings.
                </li>
                <li>
                  Refine with <code className="font-mono">update_newsletter</code>.
                </li>
                <li>
                  Send a test, then call{" "}
                  <code className="font-mono">schedule_newsletter</code>. The MCP
                  client asks the user to confirm.
                </li>
              </ol>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                {`{
  "title": "Weekly update",
  "subject": "What's new this week",
  "content": {
    "mode": "blocks",
    "blocks": {
      "selectedBlocks": [
        { "id": "hero", "name": "Rich Text" },
        { "id": "cta", "name": "Button" }
      ],
      "blockContents": {
        "hero": { "body": "<h1>Hello {{first_name}}</h1>" },
        "cta": { "text": "Read more", "href": "https://example.com" }
      }
    }
  }
}`}
              </pre>
            </section>

            <section id="html" className="space-y-4">
              <h2 className="text-3xl font-bold">HTML fallback</h2>
              <p className="text-muted-foreground">
                HTML is supported for advanced layouts. HTML newsletters are not
                editable in the visual editor and must include an unsubscribe
                link.
              </p>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                {`{
  "title": "Launch announcement",
  "subject": "We launched",
  "content": {
    "mode": "html",
    "html": "<h1>We launched</h1><a href=\\"{{unsubscribe_link}}\\">Unsubscribe</a>"
  }
}`}
              </pre>
            </section>

            <section id="scheduling" className="space-y-4">
              <h2 className="text-3xl font-bold">Scheduling safely</h2>
              <p className="text-muted-foreground">
                MCP clients ask the user to confirm through the client's
                elicitation UI before the server commits a schedule. The REST v1
                endpoint uses the same action and requires an explicit{" "}
                <code className="font-mono">confirm: true</code> field. The
                response includes the recipient count and quota impact, so agents
                can show both before sending. Pausing also asks the user to
                confirm.
              </p>
              <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
                {`{
  "newsletterId": "newsletter-id",
  "scheduledAt": "2026-11-20T09:00:00.000Z",
  "segmentId": "optional-segment-id",
  "confirm": true
}`}
              </pre>
            </section>

            <section id="discovery" className="space-y-4">
              <h2 className="text-3xl font-bold">Agent discovery</h2>
              <p className="text-muted-foreground">
                Agents can find the documentation and server metadata at these
                stable URLs:
              </p>
              <ul className="list-disc space-y-2 pl-6 font-mono text-sm">
                <li>{`${siteData.url}/docs/mcp`}</li>
                <li>{`${siteData.url}/docs/mcp.md`}</li>
                <li>{`${siteData.url}/llms.txt`}</li>
                <li>{`${siteData.url}/.well-known/mcp.json`}</li>
              </ul>
              <p className="text-muted-foreground">
                Connected agents can also call{" "}
                <code className="font-mono">get_usage_guide</code> or read the{" "}
                <code className="font-mono">sendkit://docs</code> resource.
              </p>
            </section>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-8 space-y-3 rounded-xl border p-6 text-sm">
              <p className="font-semibold">On this page</p>
              <a href="#endpoint" className="block text-muted-foreground hover:underline">
                Endpoint and authentication
              </a>
              <a href="#scopes" className="block text-muted-foreground hover:underline">
                Scopes
              </a>
              <a href="#tools" className="block text-muted-foreground hover:underline">
                Tools
              </a>
              <a href="#blocks" className="block text-muted-foreground hover:underline">
                Design with blocks
              </a>
              <a href="#html" className="block text-muted-foreground hover:underline">
                HTML fallback
              </a>
              <a href="#scheduling" className="block text-muted-foreground hover:underline">
                Scheduling safely
              </a>
              <a href="#discovery" className="block text-muted-foreground hover:underline">
                Agent discovery
              </a>
            </div>
          </aside>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
